/**
 * Invoice + plan-row status → pill-key mapping for the invoice-detail surface
 * (Phase 5.2, BILL-02 / D-21).
 *
 * Single source of truth for the invoice status pill. The detail view
 * (`InvoiceDetailView` / `ProgressBillingPlanTable`) reads its pill key through
 * this helper rather than inlining `status === 'past_due'` checks at the render
 * site (the D-43 single-source rule carried forward from Phase 4.6 / 5.5).
 *
 * This module does NOT fork {@link StatusPillKey} or touch the
 * appointment-status mapping logic (D-21). It only extends the shared union
 * with the net-new invoice/plan keys (`paid` / `open` / `partial` /
 * `past-due` / `scheduled`) and maps the invoice + plan-row status strings
 * onto them per the UI-SPEC § Color "Status-pill taxonomy".
 *
 * Taxonomy (UI-SPEC, shared with 5.1/5.3, reused verbatim):
 *
 *   | status code                          | pill key    | semantic        |
 *   | ------------------------------------ | ----------- | --------------- |
 *   | paid / paid_in_full                  | 'paid'         | success / green |
 *   | open                                 | 'open'         | info / sky-blue |
 *   | partial / partially_paid             | 'partial'      | warning / amber |
 *   | current_draw                         | 'current-draw' | warning / amber |
 *   | past_due                             | 'past-due'     | danger / red    |
 *   | scheduled                            | 'scheduled'    | info / sky-blue |
 *   | cancelled / voided                   | 'cancelled'    | muted neutral   |
 *
 * Defensive default (Threat T-5.2-03): an unknown / out-of-taxonomy status
 * returns `null` — never silently misclassify a voided/cancelled bill as paid.
 * The view omits the pill when the mapping returns `null` (quote-status
 * precedent: `mapQuoteStatusToPillKey`).
 */

import type { StatusPillKey } from './appointment-status';

/**
 * Status-code → pill-key lookup, single-sourced from the UI-SPEC taxonomy.
 * A frozen record (rather than a switch) keeps
 * {@link mapInvoiceStatusToPillKey} under the cyclomatic-complexity ceiling
 * and makes the taxonomy a flat, grep-able table. Any code NOT present here
 * maps to `null` (defensive — never silently misclassify, T-5.2-03).
 */
const STATUS_TO_PILL_KEY: Readonly<Record<string, StatusPillKey>> = {
  // invoice-level payment_status + plan-row installment statuses
  paid: 'paid',
  paid_in_full: 'paid',
  open: 'open',
  partial: 'partial',
  partially_paid: 'partial',
  // 260618-esd: current_draw is a fully-unpaid installment, not a partial
  // payment — its own amber 'Current Draw' pill (distinct copy/icon).
  current_draw: 'current-draw',
  past_due: 'past-due',
  scheduled: 'scheduled',
  cancelled: 'cancelled',
  voided: 'cancelled',
};

/**
 * Map an invoice or plan-row (installment) status string to its
 * {@link StatusPillKey}, or `null` when no pill should render.
 *
 * Covers both the invoice-level `payment_status`
 * (paid_in_full / past_due / open / partial / voided / cancelled) and the
 * plan-row installment statuses
 * (paid / partially_paid / current_draw / past_due / scheduled / cancelled).
 *
 * The lower-casing is defensive; the adapter already normalises status at the
 * wire boundary (mirrors `quote-status.ts`). Out-of-taxonomy / unknown codes
 * return `null` so the view omits the pill rather than misclassifying.
 */
export function mapInvoiceStatusToPillKey(
  status: string,
): StatusPillKey | null {
  const normalised = (status ?? '').toLowerCase();
  return STATUS_TO_PILL_KEY[normalised] ?? null;
}
